interface Player {
  id: number;
  name: string;
  lastName: string;
  gender: string;
  memberNumber: string;
  birthDate: string;
  pending?: boolean;
}

export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const [datePart] = dateString.split('T');
  const [year, month, day] = datePart.split('-');
  if (!(year && month && day)) return 'Kein Heimspiel';
  return `${day}.${month}.${year}`;
};

export const birthDate2age = (birthDate: string): number => {
  if (!birthDate) return 0;
  const today = new Date();
  const birthDateObj = new Date(birthDate);
  let age = today.getFullYear() - birthDateObj.getFullYear();
  const monthDiff = today.getMonth() - birthDateObj.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
  ) {
    age--;
  }
  return age;
};

export const countGender = (players: Player[], gender: string): number => {
  return players.filter((player) => player.gender === gender).length;
};

/**
 * Eine DGV-Mitgliedsnummer (persid) ist eine Ziffernfolge fester Länge, die mit
 * der clubNumberCountry beginnt – z.B. "0497705012530". Wird sie unterwegs als
 * Zahl behandelt (JSON, Number(), Eingabefeld), fallen die führenden Nullen weg:
 * 497705012530. Deshalb werden hier alle Nicht-Ziffern und führende Nullen
 * entfernt, damit beide Schreibweisen vergleichbar sind.
 */
export const normalizeMemberNumber = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined) return '';
  const digits = String(value).replace(/\D/g, '');
  return digits.replace(/^0+/, '');
};

/**
 * Vergleicht zwei Mitgliedsnummern unabhängig von fehlenden führenden Nullen.
 * "0497705012530" und 497705012530 gelten als identisch.
 */
export const isSameMemberNumber = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): boolean => {
  const normalizedA = normalizeMemberNumber(a);
  const normalizedB = normalizeMemberNumber(b);
  return normalizedA !== '' && normalizedA === normalizedB;
};

export const formatEuroDE = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined || value === '') return '';
  const formatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
  if (typeof value === 'number') {
    return formatter.format(value);
  }
  // Extract numeric part from strings like "100.00 €" or "1.100,00 €"
  const raw = String(value).trim();
  // Remove currency and spaces
  const cleaned = raw.replace(/[^\d.,-]/g, '');
  // If both separators exist, assume last separator is decimal
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized = cleaned;
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      // comma decimal, dots thousands
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // dot decimal, commas thousands
      normalized = cleaned.replace(/,/g, '');
    }
  } else if (lastComma > -1) {
    // Only comma present => decimal
    normalized = cleaned.replace(',', '.');
  } else {
    // Only dots or none => keep as is
    normalized = cleaned;
  }
  const num = Number.parseFloat(normalized);
  if (Number.isNaN(num)) return raw;
  return formatter.format(num);
};
